import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CurrencyInr, CheckCircle, LockSimple, ShareNetwork, UsersThree, Sparkle } from '@phosphor-icons/react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const OFFER_ICONS = {
  social_share: ShareNetwork,
  invite: UsersThree,
  promo: Sparkle,
};

const ensureRzp = () => new Promise((resolve, reject) => {
  if (window.Razorpay) return resolve();
  const s = document.createElement('script');
  s.src = 'https://checkout.razorpay.com/v1/checkout.js';
  s.onload = () => resolve();
  s.onerror = () => reject(new Error('Razorpay script failed to load'));
  document.body.appendChild(s);
});

/**
 * Joining-fee + offers card shown on the Vendor / Professional dashboard.
 *
 * Lifecycle:
 *  1. Pre-payment: shows base fee + 3 offer cards.
 *  2. Click an offer → opens Razorpay with the discounted amount.
 *  3. After successful verify, card flips to a green "Joining fee paid" state.
 *
 * Account becomes fully active when this card is paid AND KYC is approved.
 */
const JoiningFeeCard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);

  const fetchOffers = async () => {
    setLoading(true);
    try {
      const { data: d } = await axios.get(`${BACKEND_URL}/api/joining/offers`, { withCredentials: true });
      setData(d);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load joining offers');
    }
    setLoading(false);
  };

  useEffect(() => { fetchOffers(); }, []);

  const handlePay = async (offerKey) => {
    setPaying(true);
    try {
      await ensureRzp();
      const token = localStorage.getItem('token');
      const { data: order } = await axios.post(`${BACKEND_URL}/api/joining/create-order`,
        { offer: offerKey || 'none' },
        { headers: { Authorization: `Bearer ${token}` }, withCredentials: true });
      const opts = {
        key: order.key_id,
        amount: order.amount,
        currency: order.currency,
        name: 'BeeWeddy Joining Fee',
        description: order.offer_applied !== 'none' ? `${order.offer_applied} offer applied` : 'Joining fee',
        order_id: order.order_id,
        prefill: { name: order.name, email: order.email, contact: order.contact },
        theme: { color: '#B85946' },
        handler: async (resp) => {
          try {
            await axios.post(`${BACKEND_URL}/api/joining/verify-payment`, {
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            }, { headers: { Authorization: `Bearer ${token}` }, withCredentials: true });
            toast.success('Joining fee paid — welcome aboard!');
            fetchOffers();
          } catch (err) {
            toast.error(err.response?.data?.detail || 'Payment verification failed');
          }
        },
        modal: { ondismiss: () => setPaying(false) },
      };
      new window.Razorpay(opts).open();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not start payment');
    } finally {
      setPaying(false);
    }
  };

  const handleShareAndPay = async (offerKey) => {
    // Open share dialog → mark eligible → kick off payment with social_share applied.
    try {
      const share = data?.share_content || {};
      const adminCaption = (share.caption || '').trim();
      const adminLink = (share.link || '').trim();
      const adminImage = (share.image_url || '').trim();
      // Fall back to the original generic copy if admin hasn't configured share content yet.
      const url = adminLink || window.location.origin;
      const text = adminCaption || "I'm using BeeWeddy to book wedding & event services in one place — try it!";
      // The native Web Share API only takes title/text/url; image is shown
      // as a thumbnail in the share sheet on Android/iOS when reachable.
      if (navigator.share) {
        try {
          await navigator.share({ title: 'BeeWeddy', text, url });
        } catch {
          /* user cancelled — still continue so they can finish payment */
        }
      } else if (adminImage) {
        // Desktop / no native share — open WhatsApp Web with text+link so the
        // admin's chosen caption goes out unchanged.
        const wa = `https://wa.me/?text=${encodeURIComponent(`${text}\n${url}`)}`;
        window.open(wa, '_blank');
      } else {
        window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`, '_blank');
      }
      await axios.post(`${BACKEND_URL}/api/joining/social-share/mark`, {}, { withCredentials: true });
      await fetchOffers();
      // Immediately open Razorpay with social_share applied.
      handlePay(offerKey);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not mark share');
    }
  };

  if (loading || !data) {
    return (
      <Card className="bg-white border-[#E8E1D5]" data-testid="joining-fee-card-loading">
        <CardContent className="py-6 text-sm text-text-secondary text-center">Loading joining offers...</CardContent>
      </Card>
    );
  }

  if (data.joining_fee_paid) {
    return (
      <Card className="bg-green-50 border-green-300" data-testid="joining-fee-paid-card">
        <CardContent className="py-5 flex items-center gap-3">
          <CheckCircle size={28} className="text-green-700" weight="duotone" />
          <div>
            <div className="font-bold text-green-800">Joining fee paid · ₹{Number(data.joining_fee_amount_paid || 0).toLocaleString('en-IN')}</div>
            <div className="text-xs text-text-secondary">
              {data.kyc_status === 'approved'
                ? 'Your account is fully active — you can accept orders.'
                : 'You can accept orders once KYC is approved (see the KYC card above).'}
              {data.joining_offer_used && data.joining_offer_used !== 'none' && (
                <span className="ml-2 italic">Applied offer: <strong>{data.joining_offer_used}</strong></span>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-white border-[#E8E1D5]" data-testid="joining-fee-card">
      <CardHeader className="border-b border-[#E8E1D5]">
        <CardTitle className="font-heading text-xl flex items-center gap-2">
          <CurrencyInr size={22} className="text-brand-primary" /> Joining Fee
        </CardTitle>
        <CardDescription>
          Pay a one-time joining fee to activate your account and start accepting orders. Pick one offer below to lock in a discount.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4 pt-5">
        <div className="text-sm">
          Base fee: <strong className="text-base">₹{Number(data.base_fee_inr).toLocaleString('en-IN')}</strong>
        </div>

        {/* Share-content preview: shown only when admin has set up share content
            AND the social_share offer is still eligible for this user. */}
        {(() => {
          const share = data.share_content || {};
          const socialOffer = (data.offers || []).find(o => o.key === 'social_share');
          const showPreview = socialOffer?.eligible && (share.caption || share.image_url);
          if (!showPreview) return null;
          return (
            <div className="border-2 border-purple-300 bg-purple-50/40 rounded p-3 flex gap-3" data-testid="joining-share-preview">
              {share.image_url && (
                <img
                  src={share.image_url}
                  alt="Share"
                  className="w-20 h-20 object-cover rounded border border-purple-200 flex-shrink-0"
                  data-testid="joining-share-preview-img"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              )}
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-wider font-bold text-purple-700 mb-1">
                  You&apos;ll share this to unlock {socialOffer.pct}% off
                </div>
                {share.caption && (
                  <p className="text-xs text-text-primary line-clamp-3" data-testid="joining-share-preview-caption">
                    {share.caption}
                  </p>
                )}
                {share.link && (
                  <a
                    href={share.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[11px] text-purple-700 underline truncate block mt-1"
                    data-testid="joining-share-preview-link"
                  >
                    {share.link}
                  </a>
                )}
              </div>
            </div>
          );
        })()}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {data.offers.map((o) => {
            const Icon = OFFER_ICONS[o.key] || Sparkle;
            const disabled = !o.eligible || paying;
            return (
              <div key={o.key}
                className={`p-3 border-2 rounded ${o.eligible ? 'border-brand-primary/30 hover:border-brand-primary bg-brand-accent/5' : 'border-[#E8E1D5] bg-gray-50 opacity-70'}`}
                data-testid={`joining-offer-${o.key}`}>
                <div className="flex items-start gap-2 mb-1">
                  <Icon size={18} className={o.eligible ? 'text-brand-primary' : 'text-text-secondary'} weight="duotone" />
                  <div className="flex-1">
                    <div className="font-bold text-sm">{o.label}</div>
                    <div className="text-[10px] uppercase tracking-wider text-text-secondary mt-0.5">{o.pct}% OFF</div>
                  </div>
                  {!o.eligible && <LockSimple size={14} className="text-text-secondary mt-1" />}
                </div>
                <div className="text-xs text-text-secondary mb-2">{o.reason}</div>
                <div className="text-lg font-bold mb-2">
                  Final: ₹{Number(o.final_inr).toLocaleString('en-IN')}
                  {o.pct > 0 && (
                    <span className="text-text-secondary text-xs ml-2 line-through">₹{Number(data.base_fee_inr).toLocaleString('en-IN')}</span>
                  )}
                </div>
                {o.key === 'social_share' && o.eligible ? (
                  <Button disabled={disabled} size="sm"
                    onClick={() => handleShareAndPay(o.key)}
                    className="w-full bg-brand-primary hover:bg-[#A84A38] text-sand-50 rounded-none text-xs"
                    data-testid={`joining-pay-${o.key}-btn`}>
                    Share &amp; Pay ₹{Number(o.final_inr).toLocaleString('en-IN')}
                  </Button>
                ) : (
                  <Button disabled={disabled} size="sm"
                    onClick={() => handlePay(o.key)}
                    className="w-full bg-brand-primary hover:bg-[#A84A38] text-sand-50 rounded-none text-xs disabled:opacity-50"
                    data-testid={`joining-pay-${o.key}-btn`}>
                    Pay ₹{Number(o.final_inr).toLocaleString('en-IN')}
                  </Button>
                )}
              </div>
            );
          })}
        </div>

        <div className="border-t border-[#E8E1D5] pt-3 flex items-center justify-between">
          <div className="text-xs text-text-secondary italic">
            Don't want a discount? Pay the full amount instead.
          </div>
          <Button disabled={paying} onClick={() => handlePay('none')}
            variant="outline"
            className="rounded-none border-[#E8E1D5] text-xs"
            data-testid="joining-pay-none-btn">
            Pay ₹{Number(data.base_fee_inr).toLocaleString('en-IN')} (no offer)
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default JoiningFeeCard;
